import { useEffect, useMemo, useState } from "react";

import { Box, CircularProgress, Grid, Tooltip, Typography } from "@mui/material";
import { collection, getDocs } from "firebase/firestore";

import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";

const MONTHS = [
  "Jan", "Fev", "Mar", "Avr", "Mai", "Jun",
  "Jul", "Aou", "Sep", "Oct", "Nov", "Dec"
];

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const toDayIndex = jsDay => (jsDay + 6) % 7;

const SeasonalityHeatmap = ({ selectedYear }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [matrix, setMatrix] = useState(
    Array.from({ length: 12 }, () => Array(7).fill(0))
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.uid) return;
      setLoading(true);

      const nextMatrix = Array.from({ length: 12 }, () => Array(7).fill(0));

      try {
        const snapshot = await getDocs(collection(db, `users/${currentUser.uid}/orders`));

        snapshot.forEach(currentDoc => {
          const order = currentDoc.data();
          if (!order?.selectedDate) return;
          if (order.orderIsCanceled) return;

          const date = new Date(order.selectedDate);
          if (Number.isNaN(date.getTime())) return;
          if (date.getFullYear() !== selectedYear) return;

          const month = date.getMonth();
          const day = toDayIndex(date.getDay());
          nextMatrix[month][day] += 1;
        });

        setMatrix(nextMatrix);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, selectedYear]);

  const maxValue = useMemo(
    () => Math.max(1, ...matrix.flat()),
    [matrix]
  );

  const getCellColor = value => {
    if (value === 0) return "#f8fafc";
    const ratio = value / maxValue;
    if (ratio < 0.25) return "#e7eefb";
    if (ratio < 0.5) return "#c6d8f7";
    if (ratio < 0.75) return "#9bbaf0";
    return "#5b8de3";
  };

  if (loading) {
    return (
      <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container sx={{ mb: 1, pl: 7 }}>
        {DAYS.map(day => (
          <Grid item xs key={day}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {matrix.map((row, monthIndex) => (
        <Grid container key={MONTHS[monthIndex]} alignItems="center" sx={{ mb: 0.7 }}>
          <Grid item sx={{ width: 48 }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
              {MONTHS[monthIndex]}
            </Typography>
          </Grid>
          <Grid item xs>
            <Grid container spacing={0.8}>
              {row.map((value, dayIndex) => (
                <Grid item xs key={`${monthIndex}-${dayIndex}`}>
                  <Tooltip title={`${MONTHS[monthIndex]} / ${DAYS[dayIndex]} : ${value}`}>
                    <Box
                      sx={{
                        width: "100%",
                        height: 26,
                        borderRadius: 1.2,
                        border: "1px solid #e2e8f0",
                        backgroundColor: getCellColor(value),
                        transition: "transform 0.15s ease",
                        "&:hover": {
                          transform: "translateY(-1px)"
                        }
                      }}
                    />
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      ))}
    </Box>
  );
};

export default SeasonalityHeatmap;
